// Sopwith 3D - terrain geometry.
//
// The world's ground is a height per column. It becomes a slab: a top
// surface that follows the heights across a fixed depth in Z, and a front
// face down to below the bottom of the world so nothing shows underneath.
// Behind it sit two wider, dimmer ribbons that give the scene parallax.

import * as THREE from 'three';
import { Line2 } from 'three/addons/lines/Line2.js';
import { LineGeometry } from 'three/addons/lines/LineGeometry.js';
import { LineMaterial } from 'three/addons/lines/LineMaterial.js';
import { toonVertexColors } from './materials';

export interface TerrainData {
  positions: Float32Array;
  normals: Float32Array;
  colors: Float32Array;
  indices: Uint32Array;
}

export interface TerrainStyle {
  top: THREE.Color;
  topHigh: THREE.Color;
  face: THREE.Color;
}

/**
 * Build slab geometry for `heights` (one per column starting at world x
 * `x0`), `depth` deep in Z centred on z=0, with the front face going down to
 * `floor`. Pure; testable without a GPU.
 */
export function buildTerrainData(
  heights: ArrayLike<number>,
  x0: number,
  depth: number,
  floor: number,
  style: TerrainStyle,
  smooth = 6,
): TerrainData {
  const n = heights.length;
  // Shading (normals and colour) uses a box-smoothed copy of the heights so
  // single-column steps in the integer terrain do not band the surface.
  const sm = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    let sum = 0;
    let cnt = 0;
    for (let k = -smooth; k <= smooth; k++) {
      const j = i + k;
      if (j >= 0 && j < n) {
        sum += heights[j];
        cnt++;
      }
    }
    sm[i] = sum / cnt;
  }
  const half = depth / 2;
  // Per column: back-top, front-top, front-bottom (3 vertices).
  const positions = new Float32Array(n * 3 * 3);
  const normals = new Float32Array(n * 3 * 3);
  const colors = new Float32Array(n * 3 * 3);
  const indices = new Uint32Array((n - 1) * 4 * 3);

  let minH = Infinity;
  let maxH = -Infinity;
  for (let i = 0; i < n; i++) {
    minH = Math.min(minH, heights[i]);
    maxH = Math.max(maxH, heights[i]);
  }
  const range = Math.max(1, maxH - minH);
  const c = new THREE.Color();

  for (let i = 0; i < n; i++) {
    const x = x0 + i;
    const h = heights[i];
    const b = i * 9;
    // back-top
    positions[b] = x;
    positions[b + 1] = h;
    positions[b + 2] = -half;
    // front-top
    positions[b + 3] = x;
    positions[b + 4] = h;
    positions[b + 5] = half;
    // front-bottom
    positions[b + 6] = x;
    positions[b + 7] = floor;
    positions[b + 8] = half;

    // Top normal from the smoothed slope to the neighbours.
    const hl = sm[Math.max(0, i - 1)];
    const hr = sm[Math.min(n - 1, i + 1)];
    const slope = (hr - hl) / 2;
    const len = Math.hypot(slope, 1);
    normals[b] = -slope / len;
    normals[b + 1] = 1 / len;
    normals[b + 2] = 0;
    normals[b + 3] = -slope / len;
    normals[b + 4] = 1 / len;
    normals[b + 5] = 0;
    normals[b + 6] = 0;
    normals[b + 7] = 0;
    normals[b + 8] = 1;

    c.copy(style.top).lerp(style.topHigh, (sm[i] - minH) / range);
    colors[b] = c.r;
    colors[b + 1] = c.g;
    colors[b + 2] = c.b;
    colors[b + 3] = c.r;
    colors[b + 4] = c.g;
    colors[b + 5] = c.b;
    colors[b + 6] = style.face.r;
    colors[b + 7] = style.face.g;
    colors[b + 8] = style.face.b;
  }

  for (let i = 0; i < n - 1; i++) {
    const a = i * 3;
    const d = (i + 1) * 3;
    const o = i * 12;
    // top: back-top(a), front-top(a+1), back-top(d), front-top(d+1)
    indices[o] = a;
    indices[o + 1] = a + 1;
    indices[o + 2] = d + 1;
    indices[o + 3] = a;
    indices[o + 4] = d + 1;
    indices[o + 5] = d;
    // front face: front-top(a+1), front-bottom(a+2), front-top(d+1), front-bottom(d+2)
    indices[o + 6] = a + 1;
    indices[o + 7] = a + 2;
    indices[o + 8] = d + 2;
    indices[o + 9] = a + 1;
    indices[o + 10] = d + 2;
    indices[o + 11] = d + 1;
  }

  return { positions, normals, colors, indices };
}

/** Update only the heights of a geometry built by buildTerrainData. */
export function updateTerrainHeights(data: TerrainData, heights: ArrayLike<number>): void {
  const n = heights.length;
  for (let i = 0; i < n; i++) {
    const b = i * 9;
    data.positions[b + 1] = heights[i];
    data.positions[b + 4] = heights[i];
  }
}

export function terrainGeometry(data: TerrainData): THREE.BufferGeometry {
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(data.positions, 3));
  geo.setAttribute('normal', new THREE.BufferAttribute(data.normals, 3));
  geo.setAttribute('color', new THREE.BufferAttribute(data.colors, 3));
  geo.setIndex(new THREE.BufferAttribute(data.indices, 1));
  geo.computeBoundingSphere();
  return geo;
}

/** Rolling hills for a parallax backdrop layer, `n` columns from `x0`. */
export function backdropHeights(n: number, x0: number, seed: number, base: number, amp: number): Float32Array {
  const out = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const x = x0 + i;
    out[i] =
      base +
      amp *
        (0.55 * Math.sin(x / (163 + seed * 17) + seed) +
          0.3 * Math.sin(x / (71 + seed * 5) + seed * 2.3) +
          0.15 * Math.sin(x / (29 + seed) + seed * 4.1));
  }
  return out;
}

/** Positions for a silhouette line along the top edge of a height profile. */
export function profileLine(heights: ArrayLike<number>, x0: number, z: number): Float32Array {
  const n = heights.length;
  const out = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    out[i * 3] = x0 + i;
    out[i * 3 + 1] = heights[i];
    out[i * 3 + 2] = z;
  }
  return out;
}

export class Terrain {
  readonly group = new THREE.Group();
  private readonly data: TerrainData;
  private readonly geometry: THREE.BufferGeometry;
  private readonly edge: Line2;
  private readonly lineMaterials: LineMaterial[] = [];
  readonly depth: number;

  constructor(ground: Uint8Array, depth = 120) {
    this.depth = depth;
    const style: TerrainStyle = {
      top: new THREE.Color(0x3f8a2e),
      topHigh: new THREE.Color(0x9ccf58),
      face: new THREE.Color(0x5c3b21),
    };
    this.data = buildTerrainData(ground, 0, depth, -60, style);
    this.geometry = terrainGeometry(this.data);
    const mesh = new THREE.Mesh(this.geometry, toonVertexColors());
    this.group.add(mesh);

    // Thick dark silhouette along the front top edge: the cartoon outline
    // that separates ground from sky.
    this.edge = this.makeLine(profileLine(ground, 0, depth / 2 + 0.5), 0x14181c, 3);
    this.group.add(this.edge);
    this.group.add(this.makeLine(profileLine(ground, 0, -depth / 2 - 0.5), 0x1e3a1a, 1.5));

    // Parallax hills: wider than the world because they sit further back.
    const layers = [
      { z: -420, seed: 1, base: 70, amp: 45, color: 0x4f8f6a, edge: 0x2b5a45, span: 2.6 },
      { z: -980, seed: 2, base: 120, amp: 70, color: 0x7ea3c4, edge: 0x4d6f8f, span: 4.6 },
    ];
    for (const L of layers) {
      const n = Math.ceil(ground.length * L.span);
      const x0 = -Math.floor((n - ground.length) / 2);
      const h = backdropHeights(n, x0, L.seed, L.base, L.amp);
      const col = new THREE.Color(L.color);
      const d = buildTerrainData(h, x0, 200, -200, { top: col, topHigh: col, face: col });
      const m = new THREE.Mesh(terrainGeometry(d), toonVertexColors());
      m.position.z = L.z;
      const line = this.makeLine(profileLine(h, x0, 100.5), L.edge, 2);
      line.position.z = L.z;
      this.group.add(m, line);
    }
  }

  private makeLine(positions: Float32Array, color: number, width: number): Line2 {
    const geo = new LineGeometry();
    geo.setPositions(positions);
    const mat = new LineMaterial({ color, linewidth: width, worldUnits: false });
    this.lineMaterials.push(mat);
    const line = new Line2(geo, mat);
    line.computeLineDistances();
    line.frustumCulled = false;
    return line;
  }

  /** Line widths are in pixels, so the materials need the viewport size. */
  resize(width: number, height: number): void {
    for (const m of this.lineMaterials) {
      m.resolution.set(width, height);
    }
  }

  /** Reflect craters and flattening from the live ground array. */
  update(ground: Uint8Array): void {
    updateTerrainHeights(this.data, ground);
    (this.geometry.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
    this.edge.geometry.setPositions(profileLine(ground, 0, this.depth / 2 + 0.5));
  }
}
